// stores/player.js
//
// Una sola fuente de audio, tres consumidores.
//
// La radio, el vídeo de TV y **la pista de un artista en Radar** se excluyen:
// reproducir cualquiera de ellos para los otros dos. No es un detalle de
// maqueta, es la regla de producto que evita que dos audios suenen a la vez.
// (Decisión de Pablo, 31.08.26: «funciona igual que vídeo y radio; si se
// reproduce el audio del artista, se para la radio».)
//
// Se modela como **una fuente con dos consumidores** y no como dos booleanos
// independientes, porque dos booleanos se desincronizan: basta con que un
// camino olvide apagar el otro para que suenen los dos.
//
//     source: 'radio' | 'video' | 'pista' | null
//
// `isPlaying` se conserva porque lo leen la cabecera, el player y varias vistas
// que todavía no se han reescrito, pero es un derivado: significa «la fuente es
// la radio». No se escribe a mano — se cambia la fuente.
//
// ⚠️ En producción, quien dispara la pausa del vídeo es el `onStateChange` del
// IFrame Player API de YouTube, no el clic: el usuario también puede darle al
// play dentro del propio iframe.
import { defineStore } from "pinia";
import { LISTEN } from "@/services/host";

export const usePlayerStore = defineStore("player", {
  state: () => ({
    source: null,
    audioSrc: `${LISTEN}/listen/riot/radio.mp3`,

    // Cuál de las pistas de la página está sonando. Con varias fichas —un
    // artista tiene varios trabajos, cada uno con su transporte— no basta con
    // saber que suena una: hay que saber cuál, o se encienden todas.
    pista: null,
  }),

  getters: {
    isPlaying: (state) => state.source === "radio",
    isVideoPlaying: (state) => state.source === "video",
    isPistaPlaying: (state) => state.source === "pista",
    // ¿Es ESTA la que suena? Lo que pregunta cada transporte de la página.
    suena: (state) => (id) => state.source === "pista" && state.pista === id,
  },

  actions: {
    play() {
      this.source = "radio";
    },

    playVideo() {
      // Cambiar la fuente ya pausa la radio: no hay que apagarla aparte, y por
      // eso no puede quedarse encendida por descuido.
      this.source = "video";
      this.pista = null;
    },

    /** La pista de un trabajo de Radar. Para la radio y el vídeo, como ellos. */
    playPista(id) {
      this.source = "pista";
      this.pista = id;
    },

    /** Alterna esa pista: si ya sonaba, se calla; si no, suena ella sola. */
    togglePista(id) {
      if (this.source === "pista" && this.pista === id) {
        this.stop();
        return;
      }
      this.playPista(id);
    },

    stop() {
      this.source = null;
      this.pista = null;
    },

    toggle() {
      this.source = this.source === "radio" ? null : "radio";
      // Darle al play de la radio calla la pista, que es la otra mitad de la
      // regla: la exclusión va en los dos sentidos.
      if (this.source === "radio") this.pista = null;
    },

    setAudioSrc(newSrc) {
      this.audioSrc = newSrc;
    },
  },
});
